import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Trash2, AlertTriangle, Zap, CreditCard, Loader2, CheckCircle, AlertCircle, Send, XCircle, History } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { PRICING_CONFIG } from '@/config/pricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
  free: 'Free',
  starter: 'Starter',
  plus: 'Plus',
  pro: 'Pro'
};

function Account() {
  const { user, login, register, logout, token, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [subInfo, setSubInfo] = useState(null);

  // Fetch subscription info
  useEffect(() => {
    if (user && token) {
      axios.get(`${API}/payments/subscription-info`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setSubInfo(res.data)).catch(() => {});
    }
  }, [user, token]);

  // Forgot / Reset password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Reset password from URL token
  const [resetToken] = useState(searchParams.get('token'));
  const [isResetMode] = useState(window.location.pathname === '/reset-password' && !!searchParams.get('token'));
  const [isVerifyMode] = useState(window.location.pathname === '/verify-email' && !!searchParams.get('token'));
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  // Handle email verification from URL
  useEffect(() => {
    if (isVerifyMode && resetToken) {
      axios.post(`${API}/auth/verify-email`, { token: resetToken })
        .then(() => {
          toast.success('Email verified successfully!');
          if (fetchUser) fetchUser();
        })
        .catch(() => toast.error('Invalid or expired verification link'));
    }
  }, [isVerifyMode, resetToken]);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to send verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, password: newPassword });
      setResetDone(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
        navigate('/');
      } else {
        await register(email, password);
        toast.success('Account created!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `${isLogin ? 'Login' : 'Registration'} failed`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account deleted successfully');
      logout();
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(
        `${API}/payments/customer-portal`,
        null,
        {
          params: { origin_url: originUrl },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      window.location.href = res.data.url;
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('No subscription')) {
        toast.error('No active subscription. Subscribe to a plan first.');
        navigate('/credits');
      } else {
        toast.error('Failed to open subscription management');
      }
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${API}/payments/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscription canceled. Your remaining credits are still available.');
      setShowCancelConfirm(false);
      await fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const tier = user?.subscription_tier || 'free';
  const tierLabel = TIER_LABELS[tier] || 'Free';

  if (user) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12" data-testid="account-header">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FFB238] to-[#FFDA82] flex items-center justify-center mx-auto mb-4">
              <User className="w-10 h-10 text-[#0F1419]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Account Details</h1>
            <p className="text-gray-300">Manage your Visual Atmos account</p>
          </div>

          {/* Email Verification Banner */}
          {user && !user.email_verified && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6 mb-8 flex items-center justify-between" data-testid="email-verification-banner">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div>
                  <p className="text-amber-400 font-medium">Please verify your email address</p>
                  <p className="text-gray-400 text-sm">Check your inbox for a verification link.</p>
                </div>
              </div>
              <button
                onClick={handleResendVerification}
                disabled={resendingVerification}
                className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 font-medium hover:bg-amber-500/30 transition-colors flex items-center gap-2 flex-shrink-0"
                data-testid="resend-verification-button"
              >
                {resendingVerification ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Resend
              </button>
            </div>
          )}

          {/* Account Info */}
          <div className="bg-[#1A2035] rounded-2xl p-8 border border-[#222D4A] mb-8" data-testid="account-info">
            <h2 className="text-xl font-bold text-white mb-6">Your Information</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#222D4A] border border-[#2A3550]">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <span className="text-white">{user.email}</span>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Credits Balance</label>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#222D4A] border border-[#2A3550]">
                  <div className="w-8 h-8 rounded-lg bg-[#FFB238] flex items-center justify-center">
                    <Zap className="w-4 h-4 text-[#0F1419]" fill="#0F1419" />
                  </div>
                  <div>
                    <span className="text-white font-bold text-lg" data-testid="account-credits-balance">{user.credits_balance} credits</span>
                    {(user.subscription_credits > 0 || user.topup_credits > 0) && (
                      <p className="text-gray-500 text-xs mt-0.5">
                        {user.subscription_credits > 0 && <span>{user.subscription_credits} plan</span>}
                        {user.subscription_credits > 0 && user.topup_credits > 0 && <span> + </span>}
                        {user.topup_credits > 0 && <span>{user.topup_credits} top-up</span>}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Member Since</label>
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#222D4A] border border-[#2A3550]">
                  <span className="text-white">
                    {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Management */}
          <div className="bg-[#1A2035] rounded-2xl p-8 border border-[#222D4A] mb-8" data-testid="subscription-info">
            <h2 className="text-xl font-bold text-white mb-6">Subscription</h2>

            <div className={`flex items-center justify-between p-5 rounded-xl border mb-6 ${
              tier === 'free' ? 'bg-[#222D4A] border-[#2A3550]' : 'bg-green-500/5 border-green-500/20'
            }`}>
              <div>
                <p className="text-gray-400 text-sm mb-1">Current Plan</p>
                <p className="text-2xl font-bold text-white" data-testid="subscription-tier">{tierLabel}</p>
                {tier !== 'free' && (
                  <p className="text-gray-400 text-sm mt-1">
                    {PRICING_CONFIG.plans.find(p => p.id === tier)?.credits.toLocaleString()} credits/month
                  </p>
                )}
              </div>
              <div className={`px-4 py-2 rounded-full text-sm font-bold ${
                tier === 'free'
                  ? 'bg-gray-600/20 text-gray-400'
                  : user?.subscription_cancel_pending
                    ? 'bg-red-500/15 text-red-400'
                    : 'bg-green-500/15 text-green-400'
              }`} data-testid="tier-badge">
                {tier === 'free' ? 'No Active Plan' : user?.subscription_cancel_pending ? (
                  subInfo?.period_end ? `Cancels ${new Date(subInfo.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Cancels soon'
                ) : 'Active'}
              </div>
            </div>

            {/* Cancellation / Downgrade pending notice */}
            {user?.subscription_cancel_pending && tier !== 'free' && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-6" data-testid="cancel-pending-notice">
                <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-400 font-medium">
                    {user?.pending_downgrade_to
                      ? `Downgrading to ${(user.pending_downgrade_to || '').charAt(0).toUpperCase() + (user.pending_downgrade_to || '').slice(1)}`
                      : 'Your plan has been canceled'}
                  </p>
                  <p className="text-gray-400 text-sm">
                    You still have full access until{' '}
                    {subInfo?.period_end ? (
                      <span className="text-white font-medium">
                        {new Date(subInfo.period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    ) : 'the end of your billing period'}
                    . {user?.pending_downgrade_to
                      ? `After that, you can subscribe to the ${(user.pending_downgrade_to || '').charAt(0).toUpperCase() + (user.pending_downgrade_to || '').slice(1)} plan.`
                      : "After that, your account will revert to the free tier. You won't be charged again."}
                  </p>
                </div>
              </div>
            )}

            {/* Active subscription period info */}
            {tier !== 'free' && !user?.subscription_cancel_pending && subInfo?.period_end && (
              <div className="flex items-center justify-between p-4 rounded-lg bg-[#222D4A] border border-[#2A3550] mb-6" data-testid="billing-cycle-info">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-400 text-sm">Next billing date</span>
                </div>
                <span className="text-white font-medium text-sm">
                  {new Date(subInfo.period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/credits')}
                  className="flex-1 px-6 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center justify-center gap-2"
                  data-testid="buy-credits-button"
                >
                  <Zap className="w-4 h-4" fill="#0F1419" />
                  {tier === 'free' ? 'Subscribe to a Plan' : 'Upgrade or Buy Credits'}
                </button>
              </div>

              {/* Cancel subscription */}
              {tier !== 'free' && !user?.subscription_cancel_pending && (
                <>
                  {!showCancelConfirm ? (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="w-full px-6 py-3 rounded-lg bg-transparent text-gray-500 text-sm hover:text-red-400 transition-colors flex items-center justify-center gap-2"
                      data-testid="cancel-subscription-button"
                    >
                      <XCircle className="w-4 h-4" /> Cancel Subscription
                    </button>
                  ) : (
                    <div className="p-5 rounded-xl bg-red-500/5 border border-red-500/20" data-testid="cancel-confirmation">
                      <div className="flex items-start gap-3 mb-4">
                        <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-red-400 font-medium mb-1">Cancel your {tierLabel} plan?</p>
                          <p className="text-gray-400 text-sm">You'll keep access and your remaining credits until the end of your billing period. After that, your account will revert to the free tier.</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={handleCancelSubscription}
                          disabled={cancelLoading}
                          className="px-5 py-2.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                          data-testid="confirm-cancel-button"
                        >
                          {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                          Yes, Cancel
                        </button>
                        <button
                          onClick={() => setShowCancelConfirm(false)}
                          className="px-5 py-2.5 rounded-lg bg-[#222D4A] text-white font-medium hover:bg-[#2A3550] transition-colors"
                          data-testid="keep-subscription-button"
                        >
                          Keep My Plan
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Generation History — full transparency into what each generation cost */}
          <div className="bg-[#1A2035] rounded-2xl p-8 border border-[#222D4A]" data-testid="generation-history-card">
            <div className="flex items-start gap-3 mb-4">
              <History className="w-5 h-5 text-[#FFB238] mt-0.5" />
              <div>
                <h2 className="text-xl font-bold text-white">Generation History</h2>
                <p className="text-gray-400 text-sm mt-1">
                  See every video, image, sound and edit you've made — plus the exact credits each one cost. Failed generations show the refund.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/account/history')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#FFB238] text-[#0F1419] font-medium hover:bg-[#F5A800] transition-colors"
              data-testid="view-generation-history-button"
            >
              <History className="w-4 h-4" /> View Generation History
            </button>
          </div>

          {/* Danger Zone */}
          <div className="bg-[#1A2035] rounded-2xl p-8 border border-red-900/30" data-testid="danger-zone">
            <h2 className="text-xl font-bold text-red-400 mb-2">Danger Zone</h2>
            <p className="text-gray-400 text-sm mb-6">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>

            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-lg bg-red-600/10 text-red-400 border border-red-600/30 hover:bg-red-600/20 transition-colors"
                data-testid="delete-account-button"
              >
                <Trash2 className="w-4 h-4" /> Delete Account
              </button>
            ) : (
              <div className="space-y-4" data-testid="delete-confirmation">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-red-600/10 border border-red-600/30">
                  <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-medium mb-1">Are you absolutely sure?</p>
                    <p className="text-gray-400 text-sm">This will permanently delete your account, all your videos, and transaction history.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={handleDeleteAccount} className="px-6 py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors" data-testid="confirm-delete-button">
                    Yes, Delete My Account
                  </button>
                  <button onClick={() => setShowDeleteConfirm(false)} className="px-6 py-3 rounded-lg bg-[#222D4A] text-white font-medium hover:bg-[#2A3550] transition-colors" data-testid="cancel-delete-button">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Reset password page
  if (isResetMode && !user) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FFB238] to-[#FFDA82] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-10 h-10 text-[#0F1419]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Reset Password</h1>
            <p className="text-gray-300">Enter your new password below</p>
          </div>
          <div className="bg-[#1A2035] rounded-2xl p-8 border border-[#222D4A]">
            {resetDone ? (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <p className="text-white font-medium mb-4">Password reset successfully!</p>
                <button onClick={() => navigate('/account')} className="px-6 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors" data-testid="go-to-login-button">
                  Go to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 6 characters" required minLength={6}
                      className="w-full pl-12 pr-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none" data-testid="new-password-input" />
                  </div>
                </div>
                <button type="submit" disabled={resetLoading}
                  className="w-full px-6 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors disabled:opacity-50" data-testid="reset-password-button">
                  {resetLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Forgot password form
  if (showForgotPassword && !user) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FFB238] to-[#FFDA82] flex items-center justify-center mx-auto mb-4">
              <Mail className="w-10 h-10 text-[#0F1419]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Forgot Password</h1>
            <p className="text-gray-300">We'll send you a reset link</p>
          </div>
          <div className="bg-[#1A2035] rounded-2xl p-8 border border-[#222D4A]">
            {forgotSent ? (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <p className="text-white font-medium mb-2">Check your inbox</p>
                <p className="text-gray-400 text-sm mb-6">If an account with that email exists, we've sent a password reset link.</p>
                <button onClick={() => { setShowForgotPassword(false); setForgotSent(false); }}
                  className="text-[#FFB238] text-sm hover:underline" data-testid="back-to-login-link">Back to Sign In</button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" required
                      className="w-full pl-12 pr-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none" data-testid="forgot-email-input" />
                  </div>
                </div>
                <button type="submit" disabled={forgotLoading}
                  className="w-full px-6 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors disabled:opacity-50" data-testid="send-reset-link-button">
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <div className="text-center">
                  <button type="button" onClick={() => setShowForgotPassword(false)} className="text-gray-400 text-sm hover:text-white transition-colors">Back to Sign In</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8" data-testid="auth-header">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FFB238] to-[#FFDA82] flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-[#0F1419]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Welcome to Visual Atmos</h1>
          <p className="text-gray-300">Sign in with Google to get <span className="text-[#FFB238] font-semibold">40 free credits every day</span>.</p>
        </div>

        <div className="bg-[#1A2035] rounded-2xl p-8 border border-[#222D4A]" data-testid="auth-form">
          <button
            onClick={() => {
              const returnUrl = `${window.location.origin}/auth/callback`;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(returnUrl)}`;
            }}
            data-testid="google-signin-button-account"
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-lg bg-white text-gray-800 font-semibold text-base hover:bg-gray-100 transition-colors shadow-md"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            <span>Continue with Google</span>
          </button>

          <p className="text-xs text-gray-500 text-center mt-6">
            By continuing you agree to our{' '}
            <a href="/terms" className="underline hover:text-gray-300">Terms</a> and{' '}
            <a href="/privacy" className="underline hover:text-gray-300">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Account;
